import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Home, PawPrint, Share2, ShieldCheck, Sparkles, BookmarkCheck, Link2, Loader2, Check, Heart, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { findDogByFingerprint, getDog, toggleFavorite, updateHistoryEntry } from "@/lib/dogProfiles";
import { createShareLink, copyOrShare } from "@/lib/share";

// Renders the printable "Grooming Request" card and composes a downloadable
// image via a hidden canvas so we don't need html2canvas.

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function composeCardImage({ photo, preview, config }) {
  const W = 1200;
  const H = 1600;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");

  // Background
  ctx.fillStyle = "#fdfbf7";
  ctx.fillRect(0, 0, W, H);

  // Header band
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, W, 140);
  ctx.fillStyle = "#ff6b4a";
  ctx.fillRect(0, 140, W, 6);

  ctx.fillStyle = "#ffffff";
  ctx.font = "700 40px 'Outfit', 'Plus Jakarta Sans', sans-serif";
  ctx.fillText("GroomingPreview.ai", 60, 90);
  ctx.font = "600 22px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "#ff9d84";
  ctx.fillText("MY GROOMING REQUEST", 60, 120);

  // Before / After images
  const gap = 32;
  const imgW = (W - 60 * 2 - gap) / 2;
  const imgH = imgW * 0.75;
  const imgY = 200;

  const [before, after] = await Promise.all([loadImage(photo.dataUrl), loadImage(preview.previewImage)]);
  drawCover(ctx, before, 60, imgY, imgW, imgH, 20);
  drawCover(ctx, after, 60 + imgW + gap, imgY, imgW, imgH, 20);

  // Labels
  ctx.font = "700 18px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "#0f172a";
  pill(ctx, 76, imgY + 16, "BEFORE", "#0f172a", "#ffffff");
  pill(ctx, 60 + imgW + gap + 16, imgY + 16, preview.isMock ? "PREVIEW MODE" : "AI PREVIEW", "#ff6b4a", "#ffffff");

  // Meta panel
  let y = imgY + imgH + 50;
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, 60, y, W - 120, 620, 24);
  ctx.fill();
  ctx.strokeStyle = "#eae4d9";
  ctx.lineWidth = 2;
  roundRect(ctx, 60, y, W - 120, 620, 24);
  ctx.stroke();

  const px = 96;
  let py = y + 60;

  ctx.fillStyle = "#c84125";
  ctx.font = "700 16px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText("GROOMING BRIEF", px, py);
  py += 40;
  ctx.fillStyle = "#0f172a";
  ctx.font = "800 34px 'Outfit', 'Plus Jakarta Sans', sans-serif";
  wrapText(ctx, config.dogName ? `${config.dogName} — ${config.style?.name || ""}` : `${config.style?.name || "Custom Grooming"}`, px, py, W - px * 2, 38);
  py += 60;

  const rows = [
    ["Dog's Name", config.dogName || "—"],
    ["Breed / Type", config.breedUnknown ? "Owner unsure" : config.breed || "—"],
    ["Age", config.age || "—"],
    ["Selected Style", config.style?.name || "—"],
    ["Body Length", config.bodyLength || "—"],
    ["Face Style", config.faceStyle || "—"],
    ["Ear Style", config.earStyle || "—"],
    ["Tail Style", config.tailStyle || "—"],
  ];
  ctx.font = "600 20px 'Plus Jakarta Sans', sans-serif";
  const colW = (W - px * 2) / 2;
  rows.forEach((r, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const rx = px + col * colW;
    const ry = py + row * 56;
    ctx.fillStyle = "#64748b";
    ctx.font = "600 15px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(r[0].toUpperCase(), rx, ry);
    ctx.fillStyle = "#0f172a";
    ctx.font = "700 22px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(String(r[1]).slice(0, 34), rx, ry + 26);
  });
  py += Math.ceil(rows.length / 2) * 56 + 24;

  if (config.customInstructions) {
    ctx.fillStyle = "#64748b";
    ctx.font = "600 15px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("OWNER NOTES", px, py);
    py += 24;
    ctx.fillStyle = "#0f172a";
    ctx.font = "500 18px 'Plus Jakarta Sans', sans-serif";
    wrapText(ctx, config.customInstructions, px, py, W - px * 2, 24, 4);
  }

  // Footer disclaimer
  const fy = H - 180;
  ctx.fillStyle = "#0f172a";
  ctx.font = "800 26px 'Outfit', 'Plus Jakarta Sans', sans-serif";
  ctx.fillText('"I\'d like my dog groomed approximately like this."', 60, fy);
  ctx.fillStyle = "#64748b";
  ctx.font = "500 15px 'Plus Jakarta Sans', sans-serif";
  wrapText(
    ctx,
    "This image is a visual reference, not an exact grooming specification. Final results depend on coat type, coat condition, breed, grooming technique, and professional groomer assessment.",
    60,
    fy + 40,
    W - 120,
    20,
    4,
  );

  return c.toDataURL("image/jpeg", 0.92);
}

function drawCover(ctx, img, x, y, w, h, radius) {
  ctx.save();
  roundRect(ctx, x, y, w, h, radius);
  ctx.clip();
  const iw = img.width;
  const ih = img.height;
  const ratio = Math.max(w / iw, h / ih);
  const nw = iw * ratio;
  const nh = ih * ratio;
  ctx.drawImage(img, x + (w - nw) / 2, y + (h - nh) / 2, nw, nh);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function pill(ctx, x, y, text, bg, fg) {
  ctx.font = "700 18px 'Plus Jakarta Sans', sans-serif";
  const padX = 16;
  const padY = 10;
  const tw = ctx.measureText(text).width;
  const w = tw + padX * 2;
  const h = 34;
  ctx.fillStyle = bg;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + h / 2 + 1);
  ctx.textBaseline = "alphabetic";
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 6) {
  const words = String(text).split(" ");
  let line = "";
  let lines = 0;
  for (let n = 0; n < words.length; n++) {
    const test = line + words[n] + " ";
    if (ctx.measureText(test).width > maxWidth && n > 0) {
      ctx.fillText(line.trim(), x, y);
      line = words[n] + " ";
      y += lineHeight;
      lines++;
      if (lines >= maxLines - 1) {
        ctx.fillText(line.trim() + "…", x, y);
        return;
      }
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, y);
}

export default function GroomerCard({ photo, preview, config, savedDogId, savedEntryId, initialShareUrl, onShareCreated, onStartOver, onBack }) {
  const busyRef = useRef(false);
  // Fallback: derive saved state from localStorage if the prop is missing
  // (defensive — the prop-driven path is the primary source of truth).
  const [resolvedSavedId, setResolvedSavedId] = useState(savedDogId || null);
  useEffect(() => {
    if (savedDogId) {
      setResolvedSavedId(savedDogId);
      return;
    }
    const match = findDogByFingerprint({
      name: config.dogName,
      breed: config.breed,
      originalPhoto: photo?.dataUrl,
    });
    if (match?.id) setResolvedSavedId(match.id);
  }, [savedDogId, config.dogName, config.breed, photo?.dataUrl]);

  // Gentle, one-time favourite reminder for the just-saved look.
  const [isFav, setIsFav] = useState(false);
  const [favDismissed, setFavDismissed] = useState(false);
  // Optional custom name ("Rename on save") for the just-saved look.
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaved, setNameSaved] = useState(null);
  useEffect(() => {
    if (savedDogId && savedEntryId) {
      const entry = getDog(savedDogId)?.history?.find((h) => h.id === savedEntryId);
      setIsFav(!!entry?.favorite);
      setNameDraft(entry?.label || "");
      setNameSaved(entry?.label ? entry.label : null);
    }
  }, [savedDogId, savedEntryId]);

  const saveName = () => {
    if (!savedDogId || !savedEntryId) return;
    const label = nameDraft.trim().slice(0, 40);
    updateHistoryEntry(savedDogId, savedEntryId, { label });
    setNameDraft(label);
    setNameSaved(label);
    toast.success(label ? `Named "${label}"` : "Using the style name", {
      description: "You'll find this in your grooming history.",
    });
  };

  const markFavorite = () => {
    if (!savedDogId || !savedEntryId) return;
    const entry = toggleFavorite(savedDogId, savedEntryId);
    if (entry) {
      setIsFav(entry.favorite);
      if (entry.favorite) toast.success("Added to favourites", { description: "It'll show first in this dog's history." });
    }
  };

  const download = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const dataUrl = await composeCardImage({ photo, preview, config });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `dogstyle-grooming-card-${(config.dogName || "my-dog").replace(/\s+/g, "-").toLowerCase()}.jpg`;
      a.click();
      toast.success("Grooming card downloaded", { description: "Show it to your groomer." });
    } catch (e) {
      toast.error("Couldn't build the card image", { description: "Please try again." });
    } finally {
      busyRef.current = false;
    }
  };

  const [shareUrl, setShareUrl] = useState(initialShareUrl || null);
  const [linkBusy, setLinkBusy] = useState(false);

  const ensureShareLink = async () => {
    if (shareUrl) return shareUrl;
    setLinkBusy(true);
    try {
      const { url } = await createShareLink({
        beforeImage: photo.dataUrl,
        afterImage: preview.previewImage,
        style: config.style,
        dogName: config.dogName,
        breed: config.breed,
        age: config.age,
        bodyLength: config.bodyLength,
        faceStyle: config.faceStyle,
        earStyle: config.earStyle,
        tailStyle: config.tailStyle,
        customInstructions: config.customInstructions,
      });
      setShareUrl(url);
      onShareCreated?.(url);
      return url;
    } catch {
      toast.error("We couldn't create a link right now. Please try again.");
      return null;
    } finally {
      setLinkBusy(false);
    }
  };

  const copyLink = async () => {
    const url = await ensureShareLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: "Send it to your groomer — they'll see the before & after." });
    } catch {
      toast.success("Link ready", { description: url });
    }
  };

  const share = async () => {
    const url = await ensureShareLink();
    if (!url) return;
    const result = await copyOrShare(url);
    if (result === "copied") toast.success("Link copied", { description: "Sharing isn't available here, so we copied the link instead." });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 pb-20" data-testid="groomer-card-step">
      <div className="text-center fade-up">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#ffe8e0] text-[#c84125] text-xs font-bold tracking-widest uppercase px-3 py-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Step 4 · Groomer Card
        </span>
        <h1 className="font-display mt-4 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
          My Grooming Request
        </h1>
        <p className="mt-3 text-sm text-[#475569]">
          A polished card you can save, print, or show your groomer on your phone.
        </p>
        {resolvedSavedId && (
          <div
            data-testid="saved-to-my-dogs-banner"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#f3efe6] border border-[#eae4d9] px-4 py-2 text-xs font-semibold text-[#0f172a]"
          >
            <BookmarkCheck className="w-4 h-4 text-[#c84125]" />
            Saved to My Dogs ·{" "}
            <Link
              to="/dogs"
              className="underline decoration-[#ff6b4a] underline-offset-2"
              data-testid="view-my-dogs-link"
            >
              View grooming history
            </Link>
          </div>
        )}
      </div>

      {savedDogId && savedEntryId && (
        <div
          data-testid="name-look-panel"
          className="mt-4 mx-auto max-w-md rounded-2xl border border-[#eae4d9] bg-white px-4 py-3 text-left shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)] fade-up"
        >
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#0f172a]">
            <Pencil className="w-3.5 h-3.5 text-[#c84125]" /> Name this look
            <span className="font-normal text-[#94a3b8]">(optional)</span>
          </label>
          <div className="mt-2 flex items-center gap-2">
            <input
              data-testid="name-look-input"
              value={nameDraft}
              maxLength={40}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              placeholder={config.style?.name ? `e.g. Summer Cut · defaults to ${config.style.name}` : "e.g. Summer Cut"}
              className="flex-1 min-w-0 rounded-full border border-[#eae4d9] bg-[#fdfbf7] px-4 py-2.5 text-sm text-[#0f172a] outline-none focus:ring-2 focus:ring-[#ff6b4a]/25 focus:border-[#ff6b4a] transition"
            />
            <button
              type="button"
              data-testid="name-look-save-btn"
              onClick={saveName}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-4 py-2.5 transition ${
                nameSaved !== null && nameSaved === nameDraft.trim()
                  ? "bg-[#f3efe6] text-[#0f172a] border border-[#eae4d9]"
                  : "bg-[#0f172a] hover:bg-[#1e293b] text-white"
              }`}
            >
              {nameSaved !== null && nameSaved === nameDraft.trim() ? (
                <>
                  <Check className="w-3.5 h-3.5" /> Saved
                </>
              ) : (
                "Save name"
              )}
            </button>
          </div>
        </div>
      )}

      {savedDogId && savedEntryId && !favDismissed && !isFav && (
        <div
          data-testid="favorite-reminder"
          className="mt-4 mx-auto max-w-md flex items-center gap-3 rounded-2xl border border-[#ffd8cf] bg-[#fff6f3] px-4 py-3 fade-up delay-1"
        >
          <span className="w-8 h-8 rounded-full bg-white border border-[#ffd8cf] text-[#ff6b4a] flex items-center justify-center shrink-0">
            <Heart className="w-4 h-4" />
          </span>
          <p className="flex-1 min-w-0 text-xs sm:text-sm text-[#475569] text-left leading-snug">
            Love this cut? Favourite it to pin it to the top of {config.dogName ? `${config.dogName}'s` : "your dog's"} history.
          </p>
          <button
            type="button"
            data-testid="favorite-reminder-btn"
            onClick={markFavorite}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white text-xs font-semibold px-3.5 py-2 shadow-[0_6px_18px_-4px_rgba(255,107,74,0.4)] transition-all duration-200 active:scale-[0.98]"
          >
            <Heart className="w-3.5 h-3.5" /> Favourite
          </button>
          <button
            type="button"
            data-testid="favorite-reminder-dismiss"
            onClick={() => setFavDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 w-7 h-7 rounded-full text-[#94a3b8] hover:text-[#0f172a] hover:bg-white/70 flex items-center justify-center transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {savedDogId && savedEntryId && isFav && (
        <div className="mt-4 flex justify-center">
          <div
            data-testid="favorite-confirmed"
            className="inline-flex items-center gap-2 rounded-full bg-[#fff0ed] border border-[#ff6b4a] px-4 py-2 text-xs font-semibold text-[#c84125]"
          >
            <Heart className="w-3.5 h-3.5 fill-[#ff6b4a] text-[#ff6b4a]" /> Saved as a favourite
          </div>
        </div>
      )}

      <div
        data-testid="groomer-card"
        className="mt-8 rounded-3xl border border-[#eae4d9] bg-white shadow-sm overflow-hidden fade-up delay-1"
      >
        <div className="bg-[#0f172a] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <PawPrint className="w-5 h-5 text-[#ff6b4a]" />
            <span className="font-display font-bold tracking-tight">GroomingPreview.ai</span>
          </div>
          <span className="text-[10px] font-bold tracking-widest text-[#ff9d84]">
            GROOMING BRIEF
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 p-4">
          <div className="rounded-2xl overflow-hidden bg-[#f3efe6] aspect-[4/3] relative">
            <img src={photo.dataUrl} alt="Before" className="w-full h-full object-cover" />
            <span className="absolute top-2 left-2 rounded-full bg-[#0f172a] text-white text-[9px] font-bold tracking-widest px-2 py-1">BEFORE</span>
          </div>
          <div className="rounded-2xl overflow-hidden bg-[#f3efe6] aspect-[4/3] relative">
            <img src={preview.previewImage} alt="AI preview" className="w-full h-full object-cover" />
            <span className="absolute top-2 left-2 rounded-full bg-[#ff6b4a] text-white text-[9px] font-bold tracking-widest px-2 py-1">{preview.isMock ? "PREVIEW MODE" : "AI PREVIEW"}</span>
          </div>
        </div>

        <div className="px-6 pb-2">
          <h3 className="font-display text-2xl font-extrabold text-[#0f172a] tracking-tight">
            {config.dogName ? `${config.dogName} — ${config.style?.name}` : config.style?.name}
          </h3>
        </div>

        <dl className="px-6 pb-4 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <Item label="Dog's Name" value={config.dogName} />
          <Item label="Breed / Type" value={config.breedUnknown ? "Owner unsure" : config.breed} />
          <Item label="Age" value={config.age} />
          <Item label="Selected Style" value={config.style?.name} />
          <Item label="Body Length" value={config.bodyLength} />
          <Item label="Face Style" value={config.faceStyle} />
          <Item label="Ear Style" value={config.earStyle} />
          <Item label="Tail Style" value={config.tailStyle} />
        </dl>

        {config.customInstructions && (
          <div className="px-6 pb-4">
            <div className="text-[10px] font-bold tracking-widest text-[#64748b]">OWNER NOTES</div>
            <p className="mt-1 text-sm text-[#0f172a] leading-relaxed">{config.customInstructions}</p>
          </div>
        )}

        <div className="mx-6 mb-6 rounded-2xl bg-[#fdfbf7] border border-[#eae4d9] p-4">
          <p className="font-display text-lg font-bold text-[#0f172a]">
            "I'd like my dog groomed approximately like this."
          </p>
          <p className="mt-2 text-xs text-[#64748b] leading-relaxed">
            This image is a visual reference, not an exact grooming specification. Final results depend on coat type, coat condition, breed, grooming technique, and professional groomer assessment.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          data-testid="download-card-btn"
          onClick={download}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-5 py-3 shadow-[0_8px_25px_-4px_rgba(255,107,74,0.35)] transition-all duration-200 active:scale-[0.98]"
        >
          <Download className="w-4 h-4" /> Download Card
        </button>
        <button
          data-testid="share-card-btn"
          onClick={share}
          disabled={linkBusy}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-medium px-5 py-3 border border-[#eae4d9] transition disabled:opacity-60"
        >
          {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
        </button>
        <button
          data-testid="start-over-btn"
          onClick={onStartOver}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-medium px-5 py-3 border border-[#eae4d9] transition"
        >
          <Home className="w-4 h-4" /> Start Over
        </button>
      </div>

      <div
        data-testid="share-link-panel"
        className="mt-4 rounded-2xl border border-[#eae4d9] bg-white p-4 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]"
      >
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-[#f3efe6] text-[#c84125] flex items-center justify-center shrink-0">
            <Link2 className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0f172a]">Send a link to your groomer</div>
            <p className="text-xs text-[#64748b] mt-0.5">
              Anyone with the link can view the before & after and this grooming brief — nothing else.
            </p>
            {shareUrl ? (
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid="share-link-url"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 rounded-full border border-[#eae4d9] bg-[#fdfbf7] px-4 py-2.5 text-xs text-[#0f172a] outline-none"
                />
                <button
                  data-testid="copy-share-link-btn"
                  onClick={copyLink}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-xs font-semibold px-4 py-2.5 transition"
                >
                  <Check className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            ) : (
              <button
                data-testid="create-share-link-btn"
                onClick={copyLink}
                disabled={linkBusy}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-xs font-semibold px-4 py-2.5 transition disabled:opacity-60"
              >
                {linkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                {linkBusy ? "Creating link…" : "Create share link"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-[#64748b] justify-center">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span>Your photos are used only to create this preview and aren't shared publicly.</span>
      </div>
    </div>
  );
}

const Item = ({ label, value }) => (
  <div>
    <dt className="text-[10px] font-bold tracking-widest text-[#64748b]">
      {label.toUpperCase()}
    </dt>
    <dd className="mt-1 text-sm font-semibold text-[#0f172a]">{value || "—"}</dd>
  </div>
);
